/**
 * Yandex Metrica e-commerce, pushed through the `dataLayer` the counter is
 * initialised with (`ecommerce: 'dataLayer'` in YandexMetrica.tsx).
 *
 * Scope, and why it stops where it does: these pushes carry WHICH product moved
 * through WHICH step, and deliberately carry no revenue. No rail hands the
 * client the amount that was actually charged. The catalogue `price` is a USD
 * reference (`types/store.ts`, `types/tokens.ts`) while a Russian buyer pays
 * roubles through T-Bank or a Paddle-localized amount, and `localized_price` is
 * a formatted display string, not a number. Reporting the reference as revenue
 * would put confident, wrong money into the dashboard — worse than none,
 * because wrong money gets believed. The exact figure lives on the server,
 * where the webhook sees the capture; wiring it is a separate, deliberate step.
 *
 * Gated on the `.ru` host for the same reason the counter is: a visitor outside
 * Russia has no Metrica session for these events to belong to.
 */

import { isRuHost } from './api-host';

type EcommerceProduct = {
  id: string;
  name: string;
  category?: string;
  quantity: number;
};

function push(action: 'detail' | 'add' | 'purchase', products: EcommerceProduct[], id?: string) {
  if (!isRuHost()) return;
  if (typeof window === 'undefined') return;

  const w = window as typeof window & { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];

  const payload: Record<string, unknown> = { products };
  // Metrica keys a purchase by actionField.id and dedupes on it, so a repeated
  // push of the same checkout (a remount, a double completion callback) is
  // counted once instead of twice.
  if (action === 'purchase' && id) payload.actionField = { id };

  w.dataLayer.push({ ecommerce: { [action]: payload } });
}

/** The buyer opened a checkout for this product. */
export function trackEcommerceCheckoutOpened(sku: string, kind: string): void {
  push('add', [{ id: sku, name: sku, category: kind, quantity: 1 }]);
}

/** The checkout reported completion. The webhook remains the source of truth. */
export function trackEcommercePurchase(sku: string, kind: string, correlationToken: string): void {
  push('purchase', [{ id: sku, name: sku, category: kind, quantity: 1 }], correlationToken);
}
